import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useUrlParams } from '../../hooks/useUrlParams';
import DealsBoard from './DealsBoard';
import CompaniesTable from './CompaniesTable';
import ContactsTable from './ContactsTable';
import CrmSettings from './CrmSettings';
import logger from '../../utils/logger';
import ToolHelpButton from '../../components/ToolHelpButton';
import { matchesSearch } from '../../utils/search';

const emptyFilters = {
  search: '',
  status: 'all',
  date_field: 'created_at',
  date_from: '',
  date_to: ''
};

const CRM_TABS = ['dashboard', 'deals', 'companies', 'contacts', 'settings'];

function formatMoney(value) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(Number(value || 0));
}

function isRetainerDue(deal) {
  if (!deal.is_retainer || !deal.retainer_next_confirmation_at) return false;
  return new Date(deal.retainer_next_confirmation_at).getTime() <= Date.now();
}

function inDateRange(value, dateFrom, dateTo) {
  if (!dateFrom && !dateTo) return true;
  if (!value) return false;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;
  if (dateFrom && time < new Date(`${dateFrom}T00:00:00`).getTime()) return false;
  if (dateTo && time > new Date(`${dateTo}T23:59:59`).getTime()) return false;
  return true;
}

function CrmDashboard({ dashboard }) {
  const cards = [
    { label: 'Поступило', value: formatMoney(dashboard?.paid_amount) },
    { label: 'Ожидается', value: formatMoney(dashboard?.expected_amount) },
    { label: 'Сделок', value: dashboard?.total_deals || 0 },
    { label: 'В оплате', value: dashboard?.paid_deals || 0 },
    { label: 'Выиграно', value: dashboard?.won_deals || 0 },
    { label: 'Абонентских', value: dashboard?.retainer_deals || 0 },
    { label: 'Нужно подтвердить', value: dashboard?.retainer_due_count || 0 },
    { label: 'Средний чек', value: formatMoney(dashboard?.average_deal_amount) }
  ];

  return (
    <div className="crm__dashboard">
      {cards.map((card) => (
        <div className="crm__metric" key={card.label}>
          <span>{card.label}</span>
          <strong>{card.value}</strong>
        </div>
      ))}
    </div>
  );
}

function CrmFilters({ filters, onChange, onReset }) {
  const setFilter = (field, value) => onChange({ ...filters, [field]: value });

  return (
    <div className="crm__filters">
      <label className="crm__filter-search">
        Поиск
        <input
          type="search"
          value={filters.search}
          onChange={(event) => setFilter('search', event.target.value)}
          placeholder="Сделка, компания, контакт"
        />
      </label>
      <label>
        Статус
        <select value={filters.status} onChange={(event) => setFilter('status', event.target.value)}>
          <option value="all">Все</option>
          <option value="active">Активные</option>
          <option value="payment">Оплата</option>
          <option value="won">Выигранные</option>
          <option value="lost">Проигранные</option>
          <option value="retainer">Абонентские</option>
          <option value="due">Требуют подтверждения</option>
        </select>
      </label>
      <label>
        Дата
        <select value={filters.date_field} onChange={(event) => setFilter('date_field', event.target.value)}>
          <option value="created_at">Создания</option>
          <option value="updated_at">Изменения</option>
          <option value="expected_close_at">Планового закрытия</option>
        </select>
      </label>
      <label>
        С
        <input type="date" value={filters.date_from} onChange={(event) => setFilter('date_from', event.target.value)} />
      </label>
      <label>
        По
        <input type="date" value={filters.date_to} onChange={(event) => setFilter('date_to', event.target.value)} />
      </label>
      <button type="button" className="ghost" onClick={onReset}>
        Сбросить
      </button>
    </div>
  );
}

function buildDashboard(deals, stageMap) {
  const initial = {
    paid_amount: 0,
    expected_amount: 0,
    total_deals: deals.length,
    paid_deals: 0,
    won_deals: 0,
    retainer_deals: 0,
    retainer_due_count: 0,
    average_deal_amount: 0
  };

  const summary = deals.reduce((acc, deal) => {
    const stage = stageMap.get(deal.stage_id);
    const amount = Number(deal.amount || 0);
    if (stage?.is_payment || stage?.is_won) {
      acc.paid_amount += amount;
    } else if (!stage?.is_lost) {
      acc.expected_amount += amount;
    }
    if (stage?.is_payment) acc.paid_deals += 1;
    if (stage?.is_won) acc.won_deals += 1;
    if (deal.is_retainer) acc.retainer_deals += 1;
    if (isRetainerDue(deal)) acc.retainer_due_count += 1;
    return acc;
  }, initial);

  summary.average_deal_amount = deals.length
    ? deals.reduce((sum, deal) => sum + Number(deal.amount || 0), 0) / deals.length
    : 0;
  return summary;
}

function CrmSection({ onCreateTaskForDeal, onOpenTasksForDeal }) {
  const { request, showStatus } = useApi();
  // Вкладка CRM живёт в адресе (?sub=deals): F5, ссылка и «Назад» ведут туда же.
  const { get: getUrlParam, update: updateUrlParams } = useUrlParams();
  const urlTab = getUrlParam('sub');
  const activeTab = CRM_TABS.includes(urlTab) ? urlTab : 'dashboard';
  const setActiveTab = (id) => updateUrlParams({ sub: id === 'dashboard' ? '' : id }, { push: true });
  const [viewMode, setViewMode] = useState('kanban');
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [busy, setBusy] = useState(false);

  const pipelineOptions = useMemo(() => pipelines || [], [pipelines]);
  const stageOptions = useMemo(
    () => stages.filter((stage) => !selectedPipelineId || stage.pipeline_id === selectedPipelineId),
    [stages, selectedPipelineId]
  );
  const stageMap = useMemo(() => new Map(stageOptions.map((stage) => [stage.id, stage])), [stageOptions]);
  const companyMap = useMemo(() => new Map(companies.map((company) => [company.id, company])), [companies]);
  const contactMap = useMemo(() => new Map(contacts.map((contact) => [contact.id, contact])), [contacts]);

  const filteredDeals = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return deals.filter((deal) => {
      const stage = stageMap.get(deal.stage_id);
      if (filters.status === 'active' && (stage?.is_won || stage?.is_lost)) return false;
      if (filters.status === 'payment' && !stage?.is_payment) return false;
      if (filters.status === 'won' && !stage?.is_won) return false;
      if (filters.status === 'lost' && !stage?.is_lost) return false;
      if (filters.status === 'retainer' && !deal.is_retainer) return false;
      if (filters.status === 'due' && !isRetainerDue(deal)) return false;
      if (!inDateRange(deal[filters.date_field], filters.date_from, filters.date_to)) return false;

      if (!term) return true;
      const companyName = companyMap.get(deal.crm_company_id)?.name || deal.crm_company_name || '';
      const contactNames = (deal.contact_ids || [])
        .map((id) => {
          const contact = contactMap.get(id);
          return contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') : '';
        })
        .concat(deal.contact_names || [])
        .join(' ');
      return matchesSearch([deal, companyName, contactNames], term);
    });
  }, [deals, filters, stageMap, companyMap, contactMap]);

  const dashboard = useMemo(() => buildDashboard(filteredDeals, stageMap), [filteredDeals, stageMap]);

  const loadPipelines = async () => {
    const data = await request('/api/v1/crm/pipelines');
    setPipelines(data);
    if (data.length && !selectedPipelineId) {
      setSelectedPipelineId(data[0].id);
    }
  };

  const loadStages = async (pipelineId) => {
    const params = new URLSearchParams();
    if (pipelineId) params.append('pipeline_id', pipelineId);
    const data = await request(`/api/v1/crm/stages?${params.toString()}`);
    setStages(data);
  };

  const loadCompanies = async () => {
    const data = await request('/api/v1/crm/companies');
    setCompanies(data);
  };

  const loadContacts = async () => {
    const data = await request('/api/v1/crm/contacts');
    setContacts(data);
  };

  const loadDeals = async (pipelineId) => {
    const params = new URLSearchParams();
    if (pipelineId) params.append('pipeline_id', pipelineId);
    const data = await request(`/api/v1/crm/deals?${params.toString()}`);
    setDeals(data);
  };

  const refreshAll = async () => {
    setBusy(true);
    try {
      await Promise.all([loadPipelines(), loadCompanies(), loadContacts()]);
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось загрузить CRM данные.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    if (!selectedPipelineId && pipelines.length) {
      setSelectedPipelineId(pipelines[0].id);
      return;
    }
    if (selectedPipelineId) {
      loadStages(selectedPipelineId);
      loadDeals(selectedPipelineId);
    }
  }, [selectedPipelineId, pipelines]);

  const handleReload = async () => {
    await refreshAll();
    if (selectedPipelineId) {
      await Promise.all([loadStages(selectedPipelineId), loadDeals(selectedPipelineId)]);
    }
  };

  const handleDealsChange = (nextDeals) => {
    setDeals(nextDeals);
  };

  const sharedProps = {
    pipelines: pipelineOptions,
    stages: stageOptions,
    companies,
    contacts,
    deals: filteredDeals,
    allDeals: deals,
    selectedPipelineId,
    viewMode,
    busy,
    onReload: handleReload,
    onPipelineChange: setSelectedPipelineId,
    onViewChange: setViewMode,
    onDealsChange: handleDealsChange,
    onPipelinesChange: setPipelines,
    onStagesChange: setStages,
    onCompaniesChange: setCompanies,
    onContactsChange: setContacts,
    onCreateTaskForDeal,
    onOpenTasksForDeal
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <h3>CRM<ToolHelpButton toolKey="crm" /></h3>
        <div className="panel__actions">
          <button className="ghost" type="button" onClick={handleReload} disabled={busy}>
            Обновить
          </button>
        </div>
      </div>

      <div className="panel__tabs">
        <button type="button" className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
          Дашборд
        </button>
        <button type="button" className={activeTab === 'deals' ? 'active' : ''} onClick={() => setActiveTab('deals')}>
          Сделки
        </button>
        <button type="button" className={activeTab === 'companies' ? 'active' : ''} onClick={() => setActiveTab('companies')}>
          Компании
        </button>
        <button type="button" className={activeTab === 'contacts' ? 'active' : ''} onClick={() => setActiveTab('contacts')}>
          Контакты
        </button>
        <button type="button" className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>
          Настройки
        </button>
      </div>

      {(activeTab === 'dashboard' || activeTab === 'deals') && (
        <CrmFilters filters={filters} onChange={setFilters} onReset={() => setFilters(emptyFilters)} />
      )}

      {activeTab === 'dashboard' && <CrmDashboard dashboard={dashboard} />}
      {activeTab === 'deals' && <DealsBoard {...sharedProps} />}
      {activeTab === 'companies' && <CompaniesTable {...sharedProps} />}
      {activeTab === 'contacts' && <ContactsTable {...sharedProps} />}
      {activeTab === 'settings' && <CrmSettings {...sharedProps} />}
    </div>
  );
}

export default CrmSection;
